import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { useAuth } from "@/context/AuthContext";
import { useModuleData, ModuleLoadState } from "@/hooks/useModuleData";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, Plus, Trash, ArrowsClockwise, Warning, LinkBreak, Clock, ArrowSquareOut, ShieldWarning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONF_TONE = {
  verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
  high: "bg-blue-50 text-blue-700 border-blue-200",
  medium: "bg-amber-50 text-amber-700 border-amber-300",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_TONE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending_review: "bg-amber-50 text-amber-700 border-amber-300",
  broken: "bg-red-50 text-red-700 border-red-200",
  expired: "bg-red-50 text-red-700 border-red-200",
  retired: "bg-slate-100 text-slate-600 border-slate-200",
};

function Chip({ tone, children }) {
  return <span className={`inline-block text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${tone}`}>{children}</span>;
}

export default function IntelMonitorPage() {
  const { productId, product } = useProduct();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [catalog, setCatalog] = useState(null);
  const [countries, setCountries] = useState([]);
  const [rows, setRows] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [changes, setChanges] = useState([]);
  const [tab, setTab] = useState("sources");
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState({
    country_id: "", authority: "", source_type: "regulator_notice",
    url: "", confidence: "high", confidence_score: "", review_period_days: 90, notes: "",
  });
  const [changeForm, setChangeForm] = useState({
    country_id: "", source_id: "", field: "", before: "", after: "",
    modules: [], reason: "",
  });

  const m = useModuleData("intel-monitor", [
    { key: "catalog", url: `${API}/intel-monitor/catalog`, label: "Monitor catalog" },
    { key: "intel", url: `${API}/intelligence/${productId}`, label: "Country intelligence" },
    { key: "sources", url: `${API}/intel-monitor/${productId}/sources`, label: "Tracked sources" },
    { key: "alerts", url: `${API}/intel-monitor/${productId}/alerts`, label: "Currency alerts" },
    { key: "changes", url: `${API}/intel-monitor/${productId}/changes`, label: "Change log" },
  ], [productId]);

  useEffect(() => {
    if (!m.data) return;
    if (m.data.catalog) setCatalog(m.data.catalog);
    if (m.data.intel) setCountries(m.data.intel?.countries || []);
    if (m.data.sources) setRows(m.data.sources || []);
    if (m.data.alerts) setAlerts(m.data.alerts);
    if (m.data.changes) setChanges(m.data.changes || []);
  }, [m.data]);

  const loadAll = useCallback(async () => {
    if (!productId) return;
    setBusy("load");
    try {
      const [s, a, c] = await Promise.all([
        axios.get(`${API}/intel-monitor/${productId}/sources`),
        axios.get(`${API}/intel-monitor/${productId}/alerts`),
        axios.get(`${API}/intel-monitor/${productId}/changes`),
      ]);
      setRows(s.data || []); setAlerts(a.data); setChanges(c.data || []);
    } catch { toast.error("Load failed"); }
    finally { setBusy(null); }
  }, [productId]);

  const submitSource = async () => {
    if (!form.country_id || !form.authority || !form.url) return toast.error("Country, authority and URL are required");
    if (!form.url.startsWith("http")) return toast.error("URL must start with http(s)://");
    const raw = String(form.confidence_score).trim();
    if (raw !== "" && (isNaN(Number(raw)) || Number(raw) < 0 || Number(raw) > 100)) {
      return toast.error("Confidence must be a number from 0 to 100 — leave empty for 'Not assessed'.");
    }
    setBusy("save");
    try {
      await axios.post(`${API}/intel-monitor/${productId}/sources`,
        { ...form, confidence_score: raw === "" ? null : Number(raw) });
      toast.success("Source saved");
      setForm({ ...form, url: "", notes: "", confidence_score: "" });
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(null); }
  };

  const markReviewed = async (row) => {
    setBusy(`rev-${row.id}`);
    try {
      await axios.post(`${API}/intel-monitor/${productId}/sources/${row.id}/mark-reviewed`);
      toast.success("Marked reviewed"); loadAll();
    } catch { toast.error("Update failed"); }
    finally { setBusy(null); }
  };

  const delRow = async (row) => {
    if (!window.confirm("Remove this source?")) return;
    try {
      await axios.delete(`${API}/intel-monitor/${productId}/sources/${row.id}`);
      toast.success("Removed"); loadAll();
    } catch { toast.error("Delete failed"); }
  };

  const purgeInvalid = async () => {
    if (!window.confirm("Move any rows with invalid confidence to quarantine?")) return;
    setBusy("purge");
    try {
      const r = await axios.post(`${API}/intel-monitor/${productId}/purge-invalid`);
      toast.success(`${r.data.purged} row(s) quarantined`); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Purge failed"); }
    finally { setBusy(null); }
  };

  const submitChange = async () => {
    if (!changeForm.field || !changeForm.reason || changeForm.modules.length === 0) {
      return toast.error("Field, reason and at least one module are required");
    }
    if (!changeForm.source_id) {
      return toast.error("Select the verified source — approval is blocked without one.");
    }
    setBusy("change");
    try {
      await axios.post(`${API}/intel-monitor/${productId}/changes`, changeForm);
      toast.success("Change recorded");
      setChangeForm({ country_id: "", source_id: "", field: "", before: "", after: "", modules: [], reason: "" });
      loadAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : Array.isArray(detail) ? (detail[0]?.msg || JSON.stringify(detail)) : "Save failed";
      toast.error(msg);
    }
    finally { setBusy(null); }
  };

  if (m.loading || m.error) return <PageShell pageId="intel-monitor" title="Intelligence Monitor"><ModuleLoadState {...m} /></PageShell>;
  if (!catalog) return <PageShell pageId="intel-monitor" title="Intelligence Monitor"><ModuleLoadState {...m} loading currentStep="Monitor catalog" /></PageShell>;

  return (
    <PageShell pageId="intel-monitor" overline={`Intelligence Monitor · Prompt 15 · ${product?.name || ""}`}
      title="Source Currency & Change Log">
      <div className="flex items-center gap-1 border-b border-slate-200">
        {[["sources", "Sources"], ["alerts", "Alerts"], ["changes", "Change log"]].map(([id, label]) => (
          <button key={id} data-testid={`intel-tab-${id}`} onClick={() => setTab(id)}
            className={`text-xs font-semibold px-4 py-2.5 border-b-2 -mb-px transition-colors ${tab === id ? "border-slate-900 text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
            {label}
            {id === "alerts" && alerts && (alerts.totals.expired + alerts.totals.broken + alerts.totals.unverified_countries) > 0 && (
              <span className="ml-1 text-[10px] text-red-600">•</span>
            )}
          </button>
        ))}
        <button data-testid="intel-reload" onClick={loadAll} disabled={busy === "load"}
          className="ml-auto text-[11px] font-semibold text-slate-700 border border-slate-300 px-3 py-1.5 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5 my-1">
          {busy === "load" ? <CircleNotch size={11} className="animate-spin" /> : <ArrowsClockwise size={11} />} Refresh
        </button>
      </div>

      {tab === "sources" && (
        <>
          <div className="bg-white border border-slate-200 p-4 space-y-2" data-testid="source-form">
            <p className="overline-label text-slate-400">Add / update source</p>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              <Select value={form.country_id || undefined} onValueChange={(v) => setForm({ ...form, country_id: v })}>
                <SelectTrigger data-testid="src-country" className="h-8 text-xs bg-white"><SelectValue placeholder="Country" /></SelectTrigger>
                <SelectContent className="bg-white max-h-60">
                  {countries.map((c) => <SelectItem key={c.id} value={c.id}>{c.flag} {c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input data-testid="src-authority" className="h-8 text-xs" placeholder="Authority (e.g. PMDA)" value={form.authority} onChange={(e) => setForm({ ...form, authority: e.target.value })} />
              <Input className="h-8 text-xs" placeholder="Source type" value={form.source_type} onChange={(e) => setForm({ ...form, source_type: e.target.value })} />
              <Input data-testid="src-url" className="h-8 text-xs md:col-span-2" placeholder="https://..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
              <Select value={form.confidence} onValueChange={(v) => setForm({ ...form, confidence: v })}>
                <SelectTrigger data-testid="src-confidence" className="h-8 text-xs bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{catalog.confidence.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Input data-testid="src-confidence-score" type="number" min="0" max="100" className="h-8 text-xs"
                placeholder="Confidence 0-100 (empty = Not assessed)" value={form.confidence_score}
                onChange={(e) => setForm({ ...form, confidence_score: e.target.value })} />
            </div>
            <Textarea rows={1} className="text-xs" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            <div className="flex items-center gap-2">
              <Input className="h-8 text-xs w-40" placeholder="Review period (days)" value={form.review_period_days} onChange={(e) => setForm({ ...form, review_period_days: Number(e.target.value) || 90 })} />
              <button data-testid="src-save" onClick={submitSource} disabled={busy === "save"}
                className="text-[11px] font-semibold text-white bg-slate-900 hover:bg-slate-800 px-3 py-1.5 disabled:opacity-50 flex items-center gap-1.5">
                {busy === "save" ? <CircleNotch size={11} className="animate-spin" /> : <Plus size={11} />} Save source
              </button>
              {isAdmin && (
                <button data-testid="src-purge" onClick={purgeInvalid} disabled={busy === "purge"}
                  className="text-[11px] font-semibold text-amber-700 border border-amber-300 hover:bg-amber-50 px-3 py-1.5 disabled:opacity-50 flex items-center gap-1.5">
                  {busy === "purge" ? <CircleNotch size={11} className="animate-spin" /> : <ShieldWarning size={11} />} Purge invalid confidence rows
                </button>
              )}
            </div>
          </div>

          {rows.length === 0 ? (
            <div data-testid="sources-empty" className="py-12 text-center text-xs text-slate-400 border border-dashed border-slate-200">
              No sources tracked yet for {product?.name || "this product"}.
            </div>
          ) : (
            <div className="bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-xs" data-testid="sources-table">
                <thead className="bg-slate-50/60 text-[10px] uppercase tracking-widest text-slate-400">
                  <tr>
                    <th className="text-left px-3 py-2">Country</th>
                    <th className="text-left px-3 py-2">Authority</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">URL</th>
                    <th className="text-left px-3 py-2">Last checked</th>
                    <th className="text-left px-3 py-2">Next review</th>
                    <th className="text-left px-3 py-2">Reviewer</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Confidence</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`src-row-${r.id}`} className="hover:bg-slate-50/40">
                      <td className="px-3 py-2 text-slate-600">{r.country_id}</td>
                      <td className="px-3 py-2 font-semibold text-slate-800">{r.authority}</td>
                      <td className="px-3 py-2 text-slate-500">{r.source_type}</td>
                      <td className="px-3 py-2 text-blue-600 max-w-[180px] truncate">
                        <a href={r.url} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-1">
                          {r.url} <ArrowSquareOut size={9} />
                        </a>
                      </td>
                      <td className="px-3 py-2 text-slate-500 font-mono-data text-[10px]">{r.last_checked?.slice(0, 10) || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 font-mono-data text-[10px]">{r.next_review?.slice(0, 10) || "—"}</td>
                      <td className="px-3 py-2 text-slate-500 text-[10px]">{r.reviewer_email || "—"}</td>
                      <td className="px-3 py-2"><Chip tone={STATUS_TONE[r.status] || STATUS_TONE.retired}>{r.status}</Chip></td>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1">
                          <Chip tone={CONF_TONE[r.confidence] || CONF_TONE.low}>{r.confidence}</Chip>
                          {r.confidence_assessed ? (
                            <span data-testid={`conf-score-${r.id}`} className="font-mono-data text-[10px] font-bold text-slate-700">{r.confidence_display}</span>
                          ) : (
                            <span data-testid={`conf-score-${r.id}`} className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-300">Not assessed</span>
                          )}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <button data-testid={`rev-${r.id}`} onClick={() => markReviewed(r)} disabled={busy === `rev-${r.id}`}
                          className="text-[10px] font-semibold text-slate-700 border border-slate-300 px-2 py-1 hover:bg-slate-50">
                          Mark reviewed
                        </button>
                        <button data-testid={`del-${r.id}`} onClick={() => delRow(r)} className="text-slate-300 hover:text-red-600 p-1 ml-1"><Trash size={12} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === "alerts" && alerts && (
        <div data-testid="alerts-panel" className="space-y-3">
          <div className="grid grid-cols-3 bg-white border border-slate-200 divide-x divide-slate-100">
            {[
              ["Broken links", alerts.totals.broken, LinkBreak, "text-red-600"],
              ["Expired sources", alerts.totals.expired, Clock, "text-amber-600"],
              ["Unverified countries", alerts.totals.unverified_countries, Warning, "text-red-600"],
            ].map(([l, v, Icon, cls]) => (
              <div key={l} className="px-4 py-3">
                <p className={`overline-label ${v > 0 ? cls : "text-slate-400"}`}>{l}</p>
                <p className={`font-heading font-extrabold text-2xl mt-1 ${v > 0 ? cls : "text-slate-400"}`}>
                  <Icon size={14} className="inline mr-1 align-baseline" /> {v}
                </p>
              </div>
            ))}
          </div>
          {alerts.broken_links.length > 0 && (
            <section className="bg-white border border-slate-200 p-4" data-testid="alerts-broken">
              <p className="overline-label text-red-700">Broken links</p>
              <ul className="text-[11px] mt-2 space-y-1">
                {alerts.broken_links.map((b) => (
                  <li key={b.source_id} className="border-l-2 border-red-300 pl-2 text-slate-700">{b.country_id} — {b.url}</li>
                ))}
              </ul>
            </section>
          )}
          {alerts.expired_sources.length > 0 && (
            <section className="bg-white border border-slate-200 p-4" data-testid="alerts-expired">
              <p className="overline-label text-amber-700">Expired sources</p>
              <ul className="text-[11px] mt-2 space-y-1">
                {alerts.expired_sources.map((b) => (
                  <li key={b.source_id} className="border-l-2 border-amber-300 pl-2 text-slate-700">{b.country_id} · {b.authority} — {b.days_overdue} days overdue</li>
                ))}
              </ul>
            </section>
          )}
          {alerts.unverified_countries.length > 0 && (
            <section className="bg-white border border-slate-200 p-4" data-testid="alerts-unverified">
              <p className="overline-label text-red-700">Unverified countries</p>
              <ul className="text-[11px] mt-2 space-y-1">
                {alerts.unverified_countries.map((b) => (
                  <li key={b.country_id} className="border-l-2 border-red-300 pl-2 text-slate-700">{b.country_name} — no source verified inside the defined review window</li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}

      {tab === "changes" && (
        <>
          {isAdmin && (
            <div className="bg-white border border-slate-200 p-4 space-y-2" data-testid="change-form">
              <p className="overline-label text-slate-400">Record an approved change</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <Select value={changeForm.country_id || undefined} onValueChange={(v) => setChangeForm({ ...changeForm, country_id: v })}>
                  <SelectTrigger className="h-8 text-xs bg-white"><SelectValue placeholder="Country (optional)" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-60">
                    {countries.map((c) => <SelectItem key={c.id} value={c.id}>{c.flag} {c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input data-testid="chg-field" className="h-8 text-xs" placeholder="Field (e.g. hta_body)" value={changeForm.field} onChange={(e) => setChangeForm({ ...changeForm, field: e.target.value })} />
                <Input data-testid="chg-before" className="h-8 text-xs" placeholder="Previous value" value={changeForm.before} onChange={(e) => setChangeForm({ ...changeForm, before: e.target.value })} />
                <Input data-testid="chg-after" className="h-8 text-xs" placeholder="New value" value={changeForm.after} onChange={(e) => setChangeForm({ ...changeForm, after: e.target.value })} />
                <Select value={changeForm.source_id || undefined} onValueChange={(v) => setChangeForm({ ...changeForm, source_id: v })}>
                  <SelectTrigger data-testid="chg-source" className="h-8 text-xs bg-white"><SelectValue placeholder="Verified source (required)" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-60">
                    {rows.length === 0 ? <SelectItem value="__none" disabled>No sources tracked yet</SelectItem>
                      : rows.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.authority} · {s.country_id} · conf {s.confidence_display}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap gap-1">
                {catalog.modules.map((m) => (
                  <button key={m} data-testid={`chg-mod-${m}`} onClick={() => setChangeForm((v) => ({ ...v, modules: v.modules.includes(m) ? v.modules.filter((x) => x !== m) : [...v.modules, m] }))}
                    className={`text-[10px] font-semibold px-2 py-1 border ${changeForm.modules.includes(m) ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    {m}
                  </button>
                ))}
              </div>
              <Textarea data-testid="chg-reason" rows={2} className="text-xs" placeholder="Reason for the change" value={changeForm.reason} onChange={(e) => setChangeForm({ ...changeForm, reason: e.target.value })} />
              <button data-testid="chg-save" onClick={submitChange} disabled={busy === "change"}
                className="text-[11px] font-semibold text-white bg-slate-900 hover:bg-slate-800 px-3 py-1.5 disabled:opacity-50 flex items-center gap-1.5">
                {busy === "change" ? <CircleNotch size={11} className="animate-spin" /> : <Plus size={11} />} Record change
              </button>
            </div>
          )}
          {changes.length === 0 ? (
            <div data-testid="changes-empty" className="py-12 text-center text-xs text-slate-400 border border-dashed border-slate-200">
              No approved changes yet.
            </div>
          ) : (
            <div className="space-y-2">
              {changes.map((c) => (
                <div key={c.id} data-testid={`chg-${c.id}`} className="bg-white border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-[10px] text-slate-400">{c.country_id || "global"} · {new Date(c.applied_at).toLocaleString()}</p>
                      <p className="font-semibold text-slate-800 text-xs">{c.field}</p>
                    </div>
                    <div className="flex flex-wrap gap-1">{c.modules.map((m) => <Chip key={m} tone="bg-blue-50 text-blue-700 border-blue-200">{m}</Chip>)}</div>
                  </div>
                  <p className="text-[11px] text-slate-600 mt-1">
                    <b>Before:</b> <code className="font-mono-data text-red-700">{String(c.before)}</code>
                    <span className="mx-1">→</span>
                    <b>After:</b> <code className="font-mono-data text-emerald-700">{String(c.after)}</code>
                  </p>
                  <p className="text-[11px] text-slate-500 mt-1 border-l-2 border-slate-300 pl-2">{c.reason}</p>
                  <p className="text-[10px] text-slate-400 mt-1">Approved by {c.approved_by}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </PageShell>
  );
}
